/**
 * My Claims Page
 * Shows user's company ownership claims/requests
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/common/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';

interface Company {
  id: string;
  slug: string;
  nameTh: string;
  nameEn: string;
}

interface OwnershipClaim {
  id: string;
  userId: string;
  companyId: string;
  isPrimary: boolean;
  isActive: boolean;
  isApproved: boolean;
  notes: string | null;
  createdAt: string;
  company: Company;
}

interface OwnershipListResponse {
  ownerships: OwnershipClaim[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

const MyClaimsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState<OwnershipClaim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    per_page: 20,
    has_next: false,
    has_prev: false
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch claims data
  useEffect(() => {
    if (isAuthenticated) {
      fetchClaims();
    }
  }, [isAuthenticated, pagination.page]);

  const fetchClaims = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get<OwnershipListResponse>(
        `/api/ownership/claims?page=${pagination.page}&per_page=${pagination.per_page}`
      );

      setClaims(response.data.ownerships);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        per_page: response.data.per_page,
        has_next: response.data.has_next,
        has_prev: response.data.has_prev
      });
    } catch (err: any) {
      console.error('Failed to fetch claims:', err);
      setError(err.response?.data?.detail || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (claim: OwnershipClaim) => {
    if (claim.isApproved) {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircleIcon className="w-4 h-4" />
          <span>อนุมัติแล้ว</span>
        </span>
      );
    } else if (!claim.isActive) {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircleIcon className="w-4 h-4" />
          <span>ปฏิเสธ</span>
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <ClockIcon className="w-4 h-4" />
          <span>รอการอนุมัติ</span>
        </span>
      );
    }
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <>
      <SEO
        title="คำขอเป็นเจ้าของบริษัท | Thai Security Directory"
        description="ตรวจสอบสถานะคำขอเป็นเจ้าของบริษัทของคุณ"
        url="/my-claims"
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white py-8 sm:py-12">
        <div className="container-wrapper max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-neutral-900 mb-2">
                คำขอเป็นเจ้าของบริษัท
              </h1>
              <p className="text-neutral-600">
                ตรวจสอบสถานะคำขอเป็นเจ้าของบริษัทของคุณ
              </p>
            </div>

            {/* Claims List */}
            <div className="space-y-4">
              {isLoading ? (
                // Loading State
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3 text-primary-600">
                    <ArrowPathIcon className="w-6 h-6 animate-spin" />
                    <span className="text-lg font-medium">กำลังโหลดข้อมูล...</span>
                  </div>
                </div>
              ) : error ? (
                // Error State
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                  <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-red-900 mb-2">เกิดข้อผิดพลาด</h3>
                  <p className="text-red-700 mb-4">{error}</p>
                  <button
                    onClick={fetchClaims}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    ลองใหม่อีกครั้ง
                  </button>
                </div>
              ) : claims.length === 0 ? (
                // Empty State
                <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-12 text-center">
                  <BuildingOfficeIcon className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    ยังไม่มีคำขอ
                  </h3>
                  <p className="text-neutral-600 mb-6">
                    คุณยังไม่มีคำขอเป็นเจ้าของบริษัทใดๆ
                  </p>
                  <button
                    onClick={() => navigate('/search')}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    ค้นหาบริษัท
                  </button>
                </div>
              ) : (
                // Claims List
                <>
                  {claims.map((claim) => (
                    <motion.div
                      key={claim.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl shadow-sm border border-neutral-200 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-start space-x-3">
                              <BuildingOfficeIcon className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                                  {claim.company.nameTh}
                                </h3>
                                {claim.company.nameEn && (
                                  <p className="text-sm text-neutral-600 mb-2">
                                    {claim.company.nameEn}
                                  </p>
                                )}
                                <div className="flex items-center space-x-2 text-sm text-neutral-500">
                                  <ClockIcon className="w-4 h-4" />
                                  <span>ส่งคำขอเมื่อ {formatDate(claim.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4">
                            {getStatusBadge(claim)}
                          </div>
                        </div>

                        {/* Additional Info */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 border-t border-neutral-100 pt-4">
                          {claim.isPrimary && (
                            <div className="flex items-center space-x-1">
                              <CheckCircleIcon className="w-4 h-4 text-primary-600" />
                              <span>เจ้าของหลัก</span>
                            </div>
                          )}
                          {claim.notes && (
                            <div className="flex items-center space-x-1">
                              <DocumentTextIcon className="w-4 h-4 text-neutral-400" />
                              <span>หมายเหตุ: {claim.notes}</span>
                            </div>
                          )}
                        </div>

                        {/* View Company Button */}
                        <div className="mt-4 pt-4 border-t border-neutral-100">
                          <button
                            onClick={() => navigate(`/companies/${claim.company.slug}`)}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
                          >
                            ดูข้อมูลบริษัท →
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Pagination */}
                  {pagination.total > pagination.per_page && (
                    <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-neutral-200 px-6 py-4 mt-6">
                      <div className="text-sm text-neutral-600">
                        แสดง {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, pagination.total)} จาก {pagination.total} รายการ
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={!pagination.has_prev}
                          className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ก่อนหน้า
                        </button>
                        <span className="px-4 py-2 text-sm font-medium text-neutral-900">
                          หน้า {pagination.page}
                        </span>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={!pagination.has_next}
                          className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ถัดไป
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MyClaimsPage;